import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Target, Eye, Award, Globe, Users, BookOpen, ChevronLeft, ChevronRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function AboutPage() {
  const uniqueFeatures = [
    {
      icon: <Award style={{ width: '2rem', height: '2rem', color: '#ca8a04' }} />,
      title: "Expert-Certified Trainers",
      description:
        "Our instructors are certified linguists and industry professionals with years of international experience.",
    },
    {
      icon: <BookOpen style={{ width: '2rem', height: '2rem', color: '#2563eb' }} />,
      title: "Business-Focused Curriculum",
      description:
        "Structured learning programs designed specifically for workplace communication and global career growth.",
    },
    {
      icon: <Users style={{ width: '2rem', height: '2rem', color: '#ca8a04' }} />,
      title: "Cultural Awareness Training",
      description:
        "Beyond language skills, we provide cultural context and awareness for effective international communication.",
    },
    {
      icon: <Globe style={{ width: '2rem', height: '2rem', color: '#2563eb' }} />,
      title: "Global Language Services",
      description:
        "Comprehensive solutions including interpretation, translation, and localization services for businesses.",
    },
  ];

  const instructors = [
    // French Language Experts
    {
      name: "Bhagirath Kumawat",
      specialty: "French Language Expert",
      credentials: "Postgraduate in French, Jawaharlal Nehru University, New Delhi, Language Proficiency Level C1",
      description:
        "Specialized in business French with extensive experience in corporate training and diplomatic interpretation.",
    },
    // German Language Experts
    {
      name: "Prabhati",
      specialty: "German Language Expert",
      credentials: "Graduation and MA in German, Language Proficiency C1 Level",
      description:
        "Expert in German business communication with extensive experience in international corporate training.",
    },
    // {
    //   name: "Vishnu Vaishnav",
    //   specialty: "German Language Expert",
    //   credentials: "Graduation and Master's in German, Language Proficiency C1 Level",
    //   description:
    //     "Specialized in German language instruction with expertise in advanced communication skills.",
    // },
    {
      name: "Srinjoy Mitra",
      specialty: "German Language Expert",
      credentials: "Bachelor's in German Honours",
      description:
        "Dedicated German language instructor with comprehensive knowledge of German literature and culture.",
    },
    // Japanese Language Experts
    {
      name: "Rina",
      specialty: "Japanese Language Expert",
      credentials: "Japanese Language Expert, Japan Return, N1 Qualified",
      description:
        "Native-level Japanese proficiency with N1 certification and extensive experience living in Japan.",
    },
    {
      name: "Bhakar",
      specialty: "Japanese Language Expert",
      credentials: "BA, MA in Japanese Language and Literature from JNU, Language Proficiency Level N2",
      description:
        "Expert in Japanese language and literature with advanced proficiency and academic excellence.",
    },
    // Spanish Language Experts
    {
      name: "Mansi",
      specialty: "Spanish Language Expert",
      credentials: "MA in Spanish, Language Proficiency C1 Level",
      description:
        "Specialized in Spanish language instruction with advanced proficiency and cultural expertise.",
    },
    {
      name: "Paumi Gupta",
      specialty: "Spanish Language Expert",
      credentials: "Graduation and MA in Spanish, C1 Proficiency Level",
      description:
        "Expert Spanish instructor with comprehensive knowledge of Spanish language and culture.",
    },
    // Korean Language Expert
    {
      name: "Aman Kumar",
      specialty: "Korean Language Expert",
      credentials: "Bachelor's in Korean Studies, Jawaharlal Nehru University, New Delhi",
      description:
        "Specialized in Korean language and culture with academic excellence from JNU.",
    },
  ];

  const [instructorIndex, setInstructorIndex] = useState(0);
  
  // Calculate cards per view and max index based on screen size
  const getCardsPerView = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 1024) return 3; // lg
      if (window.innerWidth >= 768) return 2; // md
      return 1; // sm
    }
    return 3;
  };
  
  const [cardsPerView, setCardsPerView] = useState(getCardsPerView());
  
  useEffect(() => {
    const handleResize = () => {
      const newCardsPerView = getCardsPerView();
      setCardsPerView(newCardsPerView);
      // Reset index if it exceeds new max
      const newMax = Math.max(0, instructors.length - newCardsPerView);
      if (instructorIndex > newMax) {
        setInstructorIndex(newMax);
      }
    };
    
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [instructorIndex, instructors.length]);
  
  const maxIndex = Math.max(0, instructors.length - cardsPerView);
  const transformPercentage = 100 / cardsPerView;

  return (
    <div className="about-page">
      <Header />

      {/* Page Header */}
      <section className="page-header">
        <div className="header-content">
          <h1><span className="first-word">About Us </span> </h1>
          <p>
            Empowering global communication through expert language education
            and professional services
          </p>
        </div>
      </section>

      {/* About Description */}
      <section className="content-section bg-white">
        <div className="section-content">
          <div className="two-column">
            <div>
              <h2>Who We Are</h2>
              <div>
                <p>
                  We are an international language education institute committed
                  to helping learners and businesses communicate confidently
                  across borders. Our curriculum is designed by certified
                  linguists and industry experts to support both personal
                  communication and corporate language needs.
                </p>
                <p>
                  From beginner to advanced proficiency, we provide structured
                  learning focused on real-world workplace communication,
                  cultural awareness, and global career growth. Along with
                  language training, we provide professional interpretation,
                  translation, localization, and corporate language solutions.
                </p>
                <p>
                  Our commitment extends beyond traditional language learning to
                  encompass cultural competency, business etiquette, and
                  strategic communication skills that drive international
                  success.
                </p>
              </div>
            </div>
            <div className="flex justify-center">
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop"
                alt="Students learning languages together"
                style={{ borderRadius: '0.5rem', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="content-section bg-gray-50">
        <div className="section-content">
          <div className="grid-2">
            <div className="mission-vision-card">
              <div className="card-header">
                <div className="icon-bg blue">
                  <Target style={{ width: '2rem', height: '2rem', color: '#2563eb' }} />
                </div>
                <h3>Our Mission</h3>
              </div>
              <p>
                To bridge cultural and linguistic barriers by providing
                world-class language education and communication services that
                empower individuals and organizations to succeed in the global
                marketplace. We strive to foster meaningful cross-cultural
                connections through expert instruction and comprehensive
                language solutions.
              </p>
            </div>
            <div className="mission-vision-card">
              <div className="card-header">
                <div className="icon-bg yellow">
                  <Eye style={{ width: '2rem', height: '2rem', color: '#ca8a04' }} />
                </div>
                <h3>Our Vision</h3>
              </div>
              <p>
                To become the leading global language education institute
                recognized for excellence in multilingual communication
                training. We envision a world where language barriers no longer
                limit personal growth, business expansion, or cultural
                understanding, creating opportunities for meaningful global
                collaboration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Unique */}
      <section className="content-section bg-white">
        <div className="section-content">
          <div className="section-header">
            <h2>What Makes Us Unique</h2>
            <p>
              Our comprehensive approach combines academic excellence with
              practical business applications
            </p>
          </div>
          <div className="grid-2">
            {uniqueFeatures.map((feature, index) => (
              <div key={index} className="feature-item">
                <div className="icon-wrapper">
                  {feature.icon}
                </div>
                <div className="feature-content">
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Instructors */}
      <section className="content-section bg-blue-50">
        <div className="section-content">
          <div className="section-header">
            <h2>Meet Our Expert Instructors</h2>
            <p>
              Learn from certified professionals with international experience
            </p>
          </div>
          <div className="instructors-carousel-wrapper">
            <button
              className="carousel-nav-btn carousel-nav-left"
              onClick={() => setInstructorIndex(Math.max(0, instructorIndex - 1))}
              disabled={instructorIndex === 0}
              aria-label="Previous instructors"
            >
              <ChevronLeft style={{ width: '2rem', height: '2rem' }} />
            </button>
            <div className="instructors-carousel-container">
              <div
                className="instructors-carousel-track"
                style={{
                  transform: `translateX(-${instructorIndex * transformPercentage}%)`,
                }}
              >
                {instructors.map((instructor, index) => (
                  <div key={index} className="instructor-card">
                    <div className="instructor-header">
                      <h3>{instructor.name}</h3>
                      <p className="specialty">{instructor.specialty}</p>
                      <p className="credentials">{instructor.credentials}</p>
                    </div>
                    <p>{instructor.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <button
              className="carousel-nav-btn carousel-nav-right"
              onClick={() => setInstructorIndex(Math.min(instructorIndex + 1, maxIndex))}
              disabled={instructorIndex >= maxIndex}
              aria-label="Next instructors"
            >
              <ChevronRight style={{ width: '2rem', height: '2rem' }} />
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {/* <section className="content-section bg-white">
        <div className="section-content">
          <div className="section-header">
            <h2>What Our Students Say</h2>
            <p>Success stories from our global community</p>
          </div>
          <div className="grid-3">
            <div className="testimonial-card">
              <div className="quote">"</div>
              <p>
                The business-focused approach helped me secure a promotion in my
                multinational company. The cultural training was invaluable for
                client meetings.
              </p>
              <div className="author">
                <div className="avatar"></div>
                <div className="author-info">
                  <p className="name">Sarah Chen</p>
                  <p className="role">Business Professional</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="quote">"</div>
              <p>
                Excellent interpretation services for our international
                conferences. Professional, accurate, and culturally sensitive.
              </p>
              <div className="author">
                <div className="avatar"></div>
                <div className="author-info">
                  <p className="name">Michael Rodriguez</p>
                  <p className="role">Event Manager</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="quote">"</div>
              <p>
                From beginner to advanced proficiency in just 18 months. The
                structured curriculum and expert guidance made all the
                difference.
              </p>
              <div className="author">
                <div className="avatar"></div>
                <div className="author-info">
                  <p className="name">Anna Kowalski</p>
                  <p className="role">Graduate Student</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="cta-section">
        <div className="section-content">
          <h2>Ready to Join Our Global Community?</h2>
          <p>
            Start your journey toward fluent, confident communication
          </p>
          <div className="cta-buttons">
            <Link to="/contact#contact-form" className="btn btn-primary btn-lg">
              Enroll Now
            </Link>
            <Link to="/courses" className="btn btn-secondary btn-lg">
              Explore Courses
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}